import React, { useCallback, useContext, useEffect, useMemo } from 'react'
import ProductTypeLabel from 'components/Luxkit/Label/ProductTypeLabel'
import { rem } from 'polished'
import styled from 'styled-components'
import BodyText from 'components/Luxkit/Typography/BodyText'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'
import { pluralizeToString } from 'lib/string/pluralize'
import Tooltip from 'components/Luxkit/Tooltip'
import ClickableLink from 'components/Common/Clickable/ClickableLink'
import offerPageURL from 'lib/offer/offerPageURL'
import { mediaQueryUp } from 'components/utils/breakpoint'
import moment from 'moment'
import FormatCurrency from 'components/Common/FormatCurrency'
import LinePricetagAltIcon from 'components/Luxkit/Icons/line/LinePricetagAltIcon'
import SolidCheckIcon from 'components/Luxkit/Icons/solid/SolidCheckIcon'
import Group from 'components/utils/Group'
import Pill from 'components/Luxkit/Pill'
import LineClockDollarIcon from 'components/Luxkit/Icons/line/LineClockDollarIcon'
import Caption from 'components/Luxkit/Typography/Caption'
import Heading from 'components/Luxkit/Typography/Heading'
import { getCruiseOfferPriceVariation } from 'lib/cruises/cruiseUtils'
import { ValueWrapper } from 'components/Common/OfferPricing/styles'
import CruiseDiscountTermsAndConditions from 'components/Cruises/CruiseDiscountTermsAndConditions'
import TextButton from 'components/Luxkit/Button/TextButton'
import ResponsiveImage, { ImageParams } from 'components/Common/ResponsiveImage'
import ImageCarousel from 'components/Luxkit/Carousel/ImageCarousel'
import TrackingContext from 'contexts/trackingContext'
import { fireInteractionEvent } from 'api/googleTagManager'
import GeoContext from 'contexts/geoContext'
import { useInView } from 'react-intersection-observer'
import { productImpression } from 'analytics/enhanced-ecommerce'
import { GAImpressionFieldObject } from 'analytics/eventDefinitions'
import arrayShuffle from 'lib/array/arrayShuffle'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'

interface Props {
  offer: App.CruiseOffer;
  linkQuery?: string;
  position: number;
  onClick: (offer: App.CruiseOffer, position) => void
}

const Section = styled.div`
  .paddingPill {
    padding-bottom: ${rem(8)};
  }

`

const DepartureDateWrapper = styled.div`
  margin-top: ${rem(8)};
  display: flex;
  align-items: center;
`
const Root = styled.div`
  min-height: ${rem(600)};
  height: 100%;

  ${mediaQueryUp.tablet} {
    margin-left: none;
  }

  border: 1px solid ${({ theme }) => theme.palette.neutral.five};
  border-radius: ${props => props.theme.borderRadius.large};
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  ${Section} {
      margin-top: ${rem(16)};
      padding-left: ${rem(20)};
      padding-right: ${rem(20)};
      &:last-child {
        padding-bottom: ${rem(20)};
      }
    }
`
const DetailsCaption = styled(BodyText)`
  align-items: center;
  display: flex;
  flex-wrap: wrap;

  > * + *::before {
    color: ${({ theme }) => theme.palette.neutral.one};
    content: "·";
    margin: 0 ${rem(4)};
    speak-as: bullets;
  }
  `

const PromotionDescription = styled.div`
> * + * {
  margin-top: ${rem(4)};
}
`
const TileMediaContainer = styled.div`
  display: flex;
  position: relative;
  height: ${rem(230)};
`

function CruiseYouMayAlsoLikeTile(props: Props) {
  const { offer, linkQuery, onClick, position } = props
  const { departurePort, returnPort, itinerary, mainDepartureDetails } = offer
  const image = useMemo(() => offer.images?.[0], [offer.images])
  const hasTheSameStartAndEndPort = departurePort === returnPort
  const returnPortCity = useMemo(() => returnPort?.split(',')?.[0], [returnPort])
  const departurePortCity = useMemo(() => departurePort?.split(',')?.[0], [departurePort])
  const cities = itinerary?.map((event) => {
    const { port } = event
    if (!port) return
    return port.city || port.name.split(',')[0]
  }).filter((city) => city && city.length > 1)

  const countries = itinerary?.map((itineraryItem) => {
    const { port } = itineraryItem
    if (!port) return
    return port.countryCode
  }).filter(country => country?.length)

  const placesCount = [...new Set(cities)].length
  const countriesCount = [...new Set(countries)].length
  const { lowestOverallPriceDetails } = mainDepartureDetails
  const { promotion, discountPills } = lowestOverallPriceDetails
  const promotionDetails = promotion ?? offer.fallbackPromotion

  const [earliestDepartureDate, ...otherDepartureDates] = useMemo(() => {
    const departureDates = (offer.departureDates || [])
      .map((date) => moment(date))
      .sort((a, b) => a.unix() - b.unix())
      .map((date) => date.format('DD MMM YYYY'))

    if (mainDepartureDetails.departureDate) {
      const mainDepartureDate = moment(mainDepartureDetails.departureDate).format('DD MMM YYYY')
      return [
        ...new Set([mainDepartureDate, ...departureDates]),
      ]
    }

    return departureDates
  }, [offer.departureDates, mainDepartureDetails.departureDate])

  const {
    primaryPrice,
    secondaryPrice,
  } = getCruiseOfferPriceVariation(offer, lowestOverallPriceDetails)
  const MEDIA_IMAGE_PARAMS: ImageParams = {
    quality: 'eco',
    aspectRatio: '4:3',
    mobileWidth: '700px',
    tabletWidth: '370px',
    desktopWidth: '750px',
    largeDesktopWidth: '750px',
  }
  const tracking = useContext(TrackingContext)
  const handleOnClick = useCallback(() => {
    if (tracking) {
      fireInteractionEvent({
        ...tracking,
        action: 'Click',
        value: position,
      })
    }
    onClick(offer, position)
  }
  , [tracking, onClick, offer, position])
  const [inViewRef, inView] = useInView({ triggerOnce: true })
  const { currentCurrency } = useContext(GeoContext)
  const listId = tracking?.listId ?? 'unknown_ymal_list'
  const listName = tracking?.listName ?? 'unknown_ymal_list'

  useEffect(() => {
    const impression: GAImpressionFieldObject = {
      id: offer.id,
      name: offer.name ?? '',
      list: listName,
      position,
      quantity: 1,
    }
    if (inView) {
      productImpression(
        currentCurrency,
        [impression],
        {
          listId,
          listName,
        },
        tracking?.lere?.version,
      )
    }
  }, [inView, currentCurrency, listId, listName, offer, position, tracking?.lere])

  return (
    <ClickableLink onClick={handleOnClick} ref={inViewRef} to={`${offerPageURL(offer)}?${linkQuery}`}>
      <Root>
        <div>

          {image &&
            <TileMediaContainer>
              <ImageCarousel
              topLeft={<ProductTypeLabel productType={offer.productType} />}
              images={arrayShuffle(offer.images)}
              imageParams={MEDIA_IMAGE_PARAMS}
              eagerLoadFirstImage
              />
            </TileMediaContainer>}
          <Section>
            <BodyTextBlock variant="small">
              {hasTheSameStartAndEndPort &&
                <span>
                  Starts and ends in <b>{returnPortCity}</b>
                </span>
              }
              {!hasTheSameStartAndEndPort && <span>
                Starts in <b>{departurePortCity}</b>. Ends in <b>{returnPortCity}</b>
              </span>}
            </BodyTextBlock>
            <BodyText variant="large" weight="bold">
              {offer.name}
            </BodyText>
          </Section>
          <Section>
            <Group direction="horizontal" horizontalAlign="space-between" gap={8}>
              <Group direction="vertical" verticalAlign="space-between">
                <BodyText variant="small">
                  {offer.cruiseLine.name} · {offer.ship.name}
                </BodyText>
                <DetailsCaption variant="medium" weight="bold">
                  <div>{pluralizeToString('night', offer.duration)}</div>
                  {!!placesCount && <div>{pluralizeToString('port', placesCount)}</div>}
                  {!!countriesCount && <div>{pluralizeToString('country', countriesCount)}</div>}
                </DetailsCaption>
              </Group>
              {offer.cruiseLine.imageId &&
                <ResponsiveImage
                  id={offer.cruiseLine.imageId}
                  mobileWidth="75px"
                  tabletWidth="75px"
                  quality="eco"
                  alt={offer.cruiseLine.name}
                  role="presentation"
                />
              }
            </Group>
          </Section>
          <Section>
            {promotionDetails?.sellingPoints && (
              <PromotionDescription>
                <BodyText weight="semi-bold" variant="medium">
                  Inclusions (select packages only)
                </BodyText>
                {promotionDetails.sellingPoints.map(
                  (point, index) => {
                    const isHighlighted = index === 0 && !promotionDetails?.onBoardCredit
                    if (!isHighlighted) {
                      return (
                        <BodyTextBlock key={point} weight="normal" variant="small" startIcon={<SolidCheckIcon/>}>
                          {point}
                        </BodyTextBlock>
                      )
                    }
                  })
              }
              </PromotionDescription>
            )}
            {promotionDetails?.sellingPoints && !promotionDetails?.onBoardCredit && (
              <PromotionDescription>
                <BodyTextBlock colour="highlight" weight="bold" variant="medium" startIcon={<LinePricetagAltIcon size="S"/>}>
                  {promotionDetails.sellingPoints[0]}
                </BodyTextBlock>
              </PromotionDescription>
            )}
            {promotionDetails?.onBoardCredit && (
              <PromotionDescription>
                <BodyTextBlock colour="highlight" weight="bold" variant="medium" startIcon={<LinePricetagAltIcon size="S"/>}>
                  Up to <FormatCurrency currency={promotionDetails.onBoardCredit.currencyCode} value={promotionDetails.onBoardCredit.amount ?? 0} /> onboard credit available
                </BodyTextBlock>
              </PromotionDescription>
            )}
            <DepartureDateWrapper>
              <BodyText variant="medium" weight="bold" >
                Departing on {earliestDepartureDate}
                {!!otherDepartureDates.length && <CSSBreakpoint min="tablet">
                  <Tooltip
                    placement="top"
                    description={`Other departure dates: ${otherDepartureDates.join(', ')}`}
                  >
                    {' '}and more
                  </Tooltip>
                </CSSBreakpoint>}
              </BodyText>
            </DepartureDateWrapper>
          </Section>
        </div>
        <div>
          <Section>
            {!offer.disableDeposits && <Group direction="horizontal" className="paddingPill" >
              {promotionDetails?.deposit && <Pill kind="tertiary" variant="primary" startIcon={<LineClockDollarIcon />}>
                Secure with a {promotionDetails.deposit.formattedValue} deposit
              </Pill>}
              {!promotionDetails?.deposit && <Pill kind="tertiary" variant="primary" startIcon={<LineClockDollarIcon />}>
                Deposits available
              </Pill>}
            </Group>}
            {lowestOverallPriceDetails.cabinCategory && <Caption variant="medium" colour="neutral-three">
              {lowestOverallPriceDetails.cabinCategory} from
            </Caption>}
            <Group direction="horizontal">
              <Heading variant="heading3" as="span">
                <FormatCurrency value={primaryPrice.total} />
              </Heading>
              <BodyText variant="medium" weight="normal" as="span">
                /{primaryPrice.type}
              </BodyText>
            </Group>
            <Group tabletDirection="horizontal" direction="horizontal" gap={8} tabletGap={20} horizontalAlign="space-between" tabletHorizontalAlign="space-between">
              <Group direction="vertical">
                {!secondaryPrice && <Caption variant="medium" weight="normal"> Twin Share </Caption>}

                {secondaryPrice && <>
                  <Caption variant="large" colour="neutral-three" as="spam">
                    <FormatCurrency value={secondaryPrice.total} />
                  </Caption>
                  <Caption variant="large" colour="neutral-three" as="spam">
                    /{secondaryPrice.type} · Twin Share
                  </Caption>
                </>}
                <Caption variant="medium" colour="neutral-three">
                  Includes taxes & fees
                </Caption>
              </Group>
              <TextButton variant="default" kind="primary" size="small">View details</TextButton>
            </Group>

            {discountPills?.price && discountPills?.discountPercentage && (
              <ValueWrapper variant="large" weight="bold">
                <Caption variant="medium" weight="bold" colour="neutral-one">
                  Valued up to <FormatCurrency value={discountPills.price}/>
                </Caption>
                <Pill
                kind="primary"
                variant="success">
                  -{discountPills.discountPercentage}%</Pill>
                <CruiseDiscountTermsAndConditions />
              </ValueWrapper>
            )}
          </Section>
        </div>
      </Root>
    </ClickableLink>
  )
}

export default CruiseYouMayAlsoLikeTile
